import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Menu, ShoppingBag, User, LogOut, Flame, LayoutDashboard, BookMarked, Languages } from "lucide-react";

const LANGS = [
  { code: "en", label: "EN", full: "English" },
  { code: "hi", label: "हि", full: "हिन्दी" },
  { code: "mr", label: "म", full: "मराठी" },
  { code: "gu", label: "ગુ", full: "ગુજરાતી" },
];

function LangSwitcher() {
  const { lang, setLang } = useI18n();
  const active = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-10 border-[#E8DEC8] bg-white text-[#5C061C] gap-1.5 px-3" data-testid="language-switcher-trigger">
          <Languages className="h-4 w-4" />
          <span className="font-semibold">{active.label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel className="text-xs uppercase tracking-widest text-[#B45309]">Language</DropdownMenuLabel>
        {LANGS.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`language-switcher-${l.code}`} className={lang === l.code ? "bg-[#F4EEDF] text-[#5C061C] font-semibold" : ""}>
            <span className="mr-2 font-semibold w-6 text-[#5C061C]">{l.label}</span>{l.full}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const { items, setOpen } = useCart();
  const navigate = useNavigate();

  const nav = [
    { to: "/", label: t("nav.home"), testid: "nav-home-link" },
    { to: "/books", label: t("nav.books"), testid: "nav-books-link" },
    { to: "/shop", label: t("nav.shop"), testid: "nav-shop-link" },
    { to: "/saadhna", label: t("nav.saadhna"), testid: "nav-saadhna-link" },
    { to: "/events", label: t("nav.events"), testid: "nav-events-link" },
    { to: "/about", label: t("nav.about"), testid: "nav-about-link" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-contact-link" },
  ];

  const linkCls = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium rounded-md transition ${isActive ? "text-[#5C061C] bg-[#F4EEDF]" : "text-[#1F1A17] hover:text-[#5C061C] hover:bg-[#F4EEDF]/60"}`;

  return (
    <header className="sticky top-0 z-40 bg-[#FAF7F2]/95 backdrop-blur-md border-b border-[#E8DEC8] shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 gap-3">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
          <img src="https://customer-assets-agu9un31.emergentagent.net/job_spiritual-library-7/artifacts/cvr8qjul_logo.jpg" alt="Sri Vidya" className="h-11 w-11 rounded-full object-cover ring-2 ring-[#D97706]/60 shadow" />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif text-[#5C061C] text-lg">Sri Vidya</div>
            <div className="text-[10px] uppercase tracking-widest text-[#B45309]">Saadhna Seva Sanstha</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.testid}>{n.label}</NavLink>
          ))}
          {user && <NavLink to="/library" className={linkCls} data-testid="nav-library-link">{t("nav.library")}</NavLink>}
          {user && (user.role === "admin" || user.role === "trustee") && (
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard-link">{t("nav.dashboard")}</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <LangSwitcher />
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="cart-open-button" className="relative">
            <ShoppingBag className="h-5 w-5" />
            {items.length > 0 && <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#D97706] text-white text-xs grid place-items-center">{items.length}</span>}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 border-[#E8DEC8]" data-testid="user-menu-button">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">{user.name || user.mobile}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  <div className="font-semibold text-[#5C061C]">{user.mobile}</div>
                  <Badge className="mt-1 bg-[#5C061C]/10 text-[#5C061C] hover:bg-[#5C061C]/10">{t(`roles.${user.role}`)}</Badge>
                  {user.is_approved_member && <Badge className="ml-1 mt-1 bg-[#15803D]/10 text-[#15803D]">Core</Badge>}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" />{t("nav.profile")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/library")} data-testid="menu-library"><BookMarked className="h-4 w-4 mr-2" />{t("nav.library")}</DropdownMenuItem>
                {(user.role === "admin" || user.role === "trustee") && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />{t("nav.dashboard")}</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />{t("login.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button className="btn-saffron rounded-full h-11 px-5 hidden sm:inline-flex" onClick={() => navigate("/login")} data-testid="mobile-otp-login-button">
              <Flame className="h-4 w-4 mr-2" /> {t("login.login")}
            </Button>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#FAF7F2]">
              <div className="pt-4 space-y-1">
                {nav.map((n) => (
                  <NavLink key={n.to} to={n.to} className={({ isActive }) => `block px-4 py-3 rounded-md text-lg ${isActive ? "bg-[#F4EEDF] text-[#5C061C]" : "text-[#1F1A17]"}`}>{n.label}</NavLink>
                ))}
                {user && <NavLink to="/library" className="block px-4 py-3 rounded-md text-lg">{t("nav.library")}</NavLink>}
                {user && (user.role === "admin" || user.role === "trustee") && (
                  <NavLink to="/dashboard" className="block px-4 py-3 rounded-md text-lg">{t("nav.dashboard")}</NavLink>
                )}
                {!user && (
                  <Button className="btn-saffron rounded-full w-full h-12 mt-4" onClick={() => navigate("/login")}>
                    <Flame className="h-4 w-4 mr-2" /> {t("login.login")}
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
